import React, { useEffect, useRef } from 'react';
import { AppMode, IConsumptionChannel, IQueueInfo } from '@tymely/atoms';
import {
    isQAChannel,
    TICKET_COUNT_QUERY_KEY,
    useAppMode,
    useChannels,
    useMultiSubscriptions,
    useSelectedChannels,
    useSetSelectedChannels,
    useTicketCountQuery,
} from '@tymely/services';
import { GroupedSelectMenu } from '@tymely/components';
import { useQueryClient } from '@tanstack/react-query';

const useSubscribeSelectedChannels = () => {
    const allSelectedChannels = useSelectedChannels();
    const queryClient = useQueryClient();
    const ticketCountQuery = useTicketCountQuery();

    const channels = React.useMemo(
        () =>
            allSelectedChannels.reduce<Record<string, IConsumptionChannel>>((memo, channel) => {
                const channelName = `organization/${channel.organization.id}/${channel.name}`;
                memo[channelName] = channel;
                return memo;
            }, {}),
        [allSelectedChannels],
    );

    const channelNames = React.useMemo(() => Object.keys(channels), [channels]);

    useMultiSubscriptions<{ data: IQueueInfo }>(
        channelNames,
        ({ data }, channel) => {
            queryClient.setQueryData(
                [TICKET_COUNT_QUERY_KEY],
                [
                    ...(ticketCountQuery.data ?? []).filter(
                        (qi) => qi.org_id !== data.org_id || qi.queue_name !== channels[channel].queue_name,
                    ),
                    data,
                ],
            );
        },
        [],
    );
};

const ChannelSelector = (props: { anchorEl: HTMLElement | null; open: boolean; onClose: () => void }) => {
    const { appMode, isQaMode } = useAppMode();
    const { channels, isLoading: channelsAreLoading } = useChannels();
    const allSelectedChannels = useSelectedChannels();
    const hasInitialized = useRef(false);
    const ticketCountQuery = useTicketCountQuery();
    const setSelectedChannels = useSetSelectedChannels();
    useSubscribeSelectedChannels();

    const values = React.useMemo(() => allSelectedChannels.map((channel) => channel.uiId), [allSelectedChannels]);

    useEffect(() => {
        if (!hasInitialized.current && isQaMode && channels.length) {
            setSelectedChannels(channels.filter((ch) => isQAChannel(ch)));
            hasInitialized.current = true;
        }
    }, [isQaMode, channels]);

    const options = React.useMemo(() => {
        const orgTicketCount = (ticketCountQuery.data ?? []).reduce(
            (acc: Record<IQueueInfo['org_id'], IQueueInfo['ticket_count']>, qi) => ({
                [qi.org_id]: (acc[qi.org_id] ?? 0) + qi.ticket_count,
            }),
            {},
        );

        return ((channels || allSelectedChannels) ?? [])
            .filter((channel) => !channel.organization.disabled && !(appMode === AppMode.QA && !isQAChannel(channel)))
            .map((channel) => {
                const ticketCount = (ticketCountQuery.data ?? []).find(
                    (qi) =>
                        qi.org_id === channel.organization.id &&
                        qi.queue_name.includes(channel.queue_name.toLowerCase()),
                )?.ticket_count;

                let optLabel = channel.name;
                if (ticketCount) {
                    optLabel = optLabel + ` (${ticketCount})`;
                }

                let orgLabel = channel.organization.name;
                if (orgTicketCount[channel.organization.id]) {
                    orgLabel = orgLabel + ` (${orgTicketCount[channel.organization.id]})`;
                }

                return {
                    value: channel.uiId,
                    label: optLabel,
                    group: {
                        id: channel.organization.id,
                        label: orgLabel,
                    },
                };
            });
    }, [ticketCountQuery.data, channels, allSelectedChannels]);

    const onSelect = React.useCallback(
        (channelUiIds: string[]) => {
            setSelectedChannels(() => {
                const selectedIds = new Set(channelUiIds);
                const newChannels = channels.filter((channel) => selectedIds.has(channel.uiId));

                const uniqueChannels = Array.from(
                    new Map(newChannels.map((channel) => [channel.uiId, channel])).values(),
                );

                return uniqueChannels;
            });
        },
        [setSelectedChannels, channels],
    );

    return (
        <GroupedSelectMenu
            anchorEl={props.anchorEl}
            open={props.open}
            values={values}
            options={options}
            onSelect={onSelect}
            onClose={props.onClose}
            variant="nav"
            multiple
            selectedShownFirst
            filter={options.length > 3}
            filterPlaceholder={
                channelsAreLoading
                    ? 'Loading...'
                    : `Search${options ? ` ${options.length}` : ''}${
                          values.length ? ` (${values.length})` : ''
                      } channels...`
            }
            isLoading={channelsAreLoading}
        />
    );
};

ChannelSelector.displayName = 'ChannelSelector';
export default ChannelSelector;
